import React, { useEffect } from "react";
import Big from "big.js";
import IExchangeForm, {
  ICryptoTradeData,
  ITradingSettings,
  TExchangeFormState,
} from "./IExchangeForm";
import * as style from "./ExchangeFormStyle.module.scss"
import ExchangeAmount, {
  getTransactionFee,
} from "../ExchangeAmount/ExchangeAmount";
import WalletToPurchaseSelect from "../WalletToPurchaseSelect/WalletToPurchaseSelect";
import FundsService from "@services/Funds.service";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import StepsHeader from "@market/components/StepsHeader/StepsHeader";
import StepButtons from "@appComponents/StepButtons/StepButtons";
import ExchangeSummary from "../ExchangeSummary/ExchangeSummary";
import PlainCounter from "@appComponents/components/PlainCounter/PlainCounter";
import Confirmation from "@appComponents/Confirmation/Confirmation";
import ConfirmForm, {
  useConfirmForm,
} from "@appComponents/ConfirmForm/ConfirmForm";
import { useExchangeForm } from "./useExchangeForm";
import { FundsAppContext, FundsDispatchContext } from "@features/context";
import { MAIN_CURRENCY, MAIN_CURRENCY_LABEL } from "@bundles/consts";
import { IPurchaseSelectWallet } from "../WalletToPurchaseSelect/IWalletToPurchaseSelect";
import {
  animateScrollTo,
  catchError,
  renameMainCurrency,
} from "@features/utils/functions";
import { AxiosResponse } from "axios";
import { ITradeTokensData } from "@appInterfaces/fundsService.interfaces";
import { updateWalletsBalanceAfterCryptoTradeTransaction } from "@funds/features/action.funds";
import { connect } from "react-redux";
import { IUpdateWalletsData } from "@funds/interfaces/IFundsReducer";
import { addInOutItem } from "../InOutHistory/InOutHistory";
import TradingInfoNotice from "../TradingInfoNotice/TradingInfoNotice";

const stepsHeader = ["Select Amount", "Summary", "Confirmation"];
const numberOfSteps = stepsHeader.length;
const TradingInfoCurrencies = ["bfusd", "btc", "eth"];

const exchangeSupportedCurrencies: IPurchaseSelectWallet[] = [
  {
    id: 1,
    currency: "btc",
    currency_name: "Bitcoin",
  },
  {
    id: 2,
    currency: "eth",
    currency_name: "Ethereum",
  },
  {
    id: 3,
    currency: MAIN_CURRENCY,
    currency_name: MAIN_CURRENCY_LABEL.toUpperCase(),
  },
];

const ExchangeForm: React.FunctionComponent<IExchangeForm> = (props) => {
  const { appLink } = React.useContext(FundsAppContext);

  if (process.env.ENABLE_LOCK_ON_BINANCE_TRADING) {
    window.location.href = appLink;
  }

  const { values, errors, dispatch } = useExchangeForm(
    props.userWallets,
    props.currency,
    props.type
  );
  const { confirmFormState, setConfirmFormState } = useConfirmForm();
  const isComingFinalStep = values.step === numberOfSteps - 1;
  const selectCurrencies = exchangeSupportedCurrencies.filter(
    (el) => el.currency !== props.currency
  );

  const getIncomeCurrency = (
    outcomeCurrency: TExchangeFormState["values"]["outcomeCurrency"]
  ) =>
    outcomeCurrency === props.currency ? values.selectCurrency : props.currency;
  const incomeCurrency = getIncomeCurrency(values.outcomeCurrency);

  useEffect(() => {
    const setTradingSettings = () => {
      FundsService.getTradingSettings()
        .then((res) => {
          const settings: NonNullable<
            TExchangeFormState["values"]["settings"]
          > = {
            all: res.data,
            current: null,
          };

          dispatch({
            type: "edit",
            payload: {
              name: "settings",
              value: settings,
            },
          });
        })
        .catch((err) => {
          catchError(err, appLink);
        });
    };

    dispatch({
      type: "resetState",
      payload: { name: "resetState", value: null },
    });
    setTradingSettings();
  }, [props.type]);

  useEffect(() => {
    const setTradingExchangeRates = () => {
      if (values.selectCurrency) {
        if (incomeCurrency && values.outcomeCurrency) {
          FundsService.getTradingExchangeRates(
            incomeCurrency,
            values.outcomeCurrency
          )
            .then((res) => {
              dispatch({
                type: "edit",
                payload: {
                  name: "exchangeRate",
                  value: res.data.body.exchange_rate,
                },
              });
            })
            .catch((err) => {
              catchError(err);
            });
        }

        if (values.settings?.all) {
          const current = getCurrentSettings(
            incomeCurrency,
            values.outcomeCurrency,
            values.settings.all
          );
          dispatch({
            type: "edit",
            payload: {
              name: "settings",
              value: {
                all: values.settings.all,
                current: current,
              },
            },
          });
        }
      }
    };

    setTradingExchangeRates();
  }, [values.selectCurrency, values.exchangeRateChangeTime]);

  const getIncomeAmount =
    values.exchangeRate && values.outcomeAmount
      ? Number(Big(values.outcomeAmount).times(values.exchangeRate))
      : 0;

  const onSelectWalletUpdate = (currency: string) => {
    dispatch({
      type: "edit",
      payload: { name: "selectCurrency", value: currency },
    });
  };

  const getCurrentSettings = (
    incomeCurrency: TExchangeFormState["values"]["outcomeCurrency"],
    outcomeCurrency: TExchangeFormState["values"]["outcomeCurrency"],
    settings: ITradingSettings[]
  ) => {
    if (incomeCurrency && outcomeCurrency) {
      const currentSettings = settings.find(
        (el) =>
          el.outcome_currency === outcomeCurrency &&
          el.income_currency === incomeCurrency
      );

      if (currentSettings) {
        const roundTo =
          currentSettings.outcome_currency === MAIN_CURRENCY ? 2 : 8;
        const roundedCurrentSettings = {
          income_currency: currentSettings.income_currency,
          outcome_currency: currentSettings.outcome_currency,
          maximum_amount_per_trade: Number(
            currentSettings.maximum_amount_per_trade
          ).toFixed(roundTo),
          minimum_amount_per_trade: Number(
            currentSettings.minimum_amount_per_trade
          ).toFixed(roundTo),
          percent_fee: currentSettings.percent_fee,
        };

        return roundedCurrentSettings;
      }
    }

    return null;
  };

  const updateExchangeRateDate = () => {
    setTimeout(() => {
      dispatch({
        type: "updateExchangeRateDate",
        payload: { name: "exchangeRateChangeTime", value: null },
      });
    }, 0);
  };

  const updateStep = (step: number) => {
    dispatch({ type: "edit", payload: { name: "step", value: step } });
    animateScrollTo(undefined, document.getElementById("exchangeForm"), 20);
  };

  const nextStepClick = () => {
    if (isComingFinalStep) {
      setConfirmFormState(false, true);
      return;
    }

    if (values.step === 1) {
      updateStep(2);
    } else if (values.step < numberOfSteps) {
      updateStep(values.step + 1);
    }
  };

  const prevStepClick = () => {
    if (values.step === 1) return;

    updateStep(values.step - 1);
  };

  const tradeTokens = () => {
    const data: ICryptoTradeData = {
      income_currency: incomeCurrency,
      outcome_currency: values.outcomeCurrency,
      outcome_amount_with_fee: values.outcomeAmount,
    };

    setConfirmFormState(true, false);

    FundsService.tradeTokens(data)
      .then((res: AxiosResponse<ITradeTokensData>) => {
        setConfirmFormState(false, false);
        const approvedData = {
          incomeAmount: res.data.income_amount,
          incomeCurrency: res.data.income_currency,
          outcomeAmount: res.data.outcome_amount,
          outcomeCurrency: res.data.outcome_currency,
        };
        props.updateWalletsAfterTransaction(approvedData);
        updateStep(3);
        addInOutItem!(res.data);
      })
      .catch((err) => {
        catchError(err);
        setConfirmFormState(false, false);
      });
  };

  const renderContent = () => {
    const priceFor1Token =
      props.type === "sell"
        ? values.exchangeRate || 0
        : Number(values.exchangeRate)
        ? Number(Big(1).div(Number(values.exchangeRate)))
        : 0;

    return props.type === "sell" ? (
      <>
        <ExchangeAmount
          currency={props.currency}
          outcomeAmount={values.outcomeAmount}
          errors={errors}
          feePercent={values.settings?.current?.percent_fee || 0}
          type="sell"
        />
        <WalletToPurchaseSelect
          availableToPayWallets={selectCurrencies}
          handleUpdate={onSelectWalletUpdate}
          currencyToPay={values.selectCurrency || ""}
          currency={props.currency}
          priceFor1Token={priceFor1Token}
          label="I want to receive:"
          isInitialValue={true}
        />
      </>
    ) : (
      <div className={style.buyContent}>
        <WalletToPurchaseSelect
          availableToPayWallets={selectCurrencies}
          handleUpdate={onSelectWalletUpdate}
          currencyToPay={values.selectCurrency || ""}
          currency={props.currency}
          priceFor1Token={priceFor1Token}
          label={`To get ${renameMainCurrency(
            props.currency
          ).toUpperCase()} I want to trade with:`}
          isInitialValue={true}
        />
        <ExchangeAmount
          currency={values.selectCurrency || ""}
          outcomeAmount={values.outcomeAmount}
          errors={errors}
          feePercent={values.settings?.current?.percent_fee || 0}
          type="trade"
        />
      </div>
    );
  };

  const renderSteps = () => {
    const feeForSummary = getTransactionFee(
      values.outcomeAmount || 0,
      values.settings?.current?.percent_fee || 0,
      true
    ) as number;

    switch (values.step) {
      case 1:
      default:
        return (
          <FundsDispatchContext.Provider value={dispatch}>
            {renderContent()}
            <p className={style.receiveFunds}>
              You will receive approximately:&nbsp;
              {getIncomeAmount && values.selectCurrency && incomeCurrency ? (
                <CurrencyElement
                  value={getIncomeAmount}
                  currency={incomeCurrency}
                />
              ) : (
                <span>-</span>
              )}
            </p>
          </FundsDispatchContext.Provider>
        );

      case 2:
        return (
          <ExchangeSummary
            outcomeAmount={values.outcomeAmount || 0}
            outcomeCurrency={values.outcomeCurrency || ""}
            incomeAmount={getIncomeAmount}
            incomeCurrency={incomeCurrency || ""}
            currency={props.currency}
            selectCurrency={values.selectCurrency || ""}
            fee={feeForSummary}
            exchangeRate={values.exchangeRate || 0}
            type={props.type === "buy" ? "trade" : "sell"}
            isApprox={true}
          />
        );
      case 3:
        return (
          <Confirmation
            data={{
              title: "Trade Completed",
              texts: [
                "Congratulations. Trade transfer has been completed and you can find your new funds in your wallet.",
              ],
              link1: {
                text: "Back to Funds",
                url: appLink,
              },
            }}
          >
            <ExchangeSummary
              outcomeAmount={values.outcomeAmount || 0}
              outcomeCurrency={values.outcomeCurrency || ""}
              incomeAmount={getIncomeAmount}
              incomeCurrency={incomeCurrency || ""}
              currency={props.currency}
              selectCurrency={values.selectCurrency || ""}
              fee={feeForSummary}
              exchangeRate={values.exchangeRate || 0}
              type={props.type === "buy" ? "trade" : "sell"}
              isApprox={false}
            />
          </Confirmation>
        );
    }
  };

  return (
    <div id="exchangeForm">
      {TradingInfoCurrencies.includes(props.currency) && <TradingInfoNotice />}
      {TradingInfoCurrencies.includes(props.currency) && (
        <TradingInfoNotice currency={props.currency} />
      )}
      <StepsHeader current={values.step} steps={stepsHeader} />
      {values.step < 3 && (
        <PlainCounter
          completedText={"0 sec"}
          onComplete={updateExchangeRateDate}
          timeLeft={values.exchangeRateChangeTime.toString()}
          abbreviated={true}
          beforeText={"Exchange rate updates in: "}
          addedClassName={style.counter}
          key={values.exchangeRateChangeTime.toString()}
        />
      )}
      <div className={style.contentHolder}>{renderSteps()}</div>
      {values.step < numberOfSteps && (
        <StepButtons
          nextStep={nextStepClick}
          prevStep={prevStepClick}
          isFirstStep={values.step === 1}
          nextStepEnable={
            Object.keys(errors).length === 0 && !confirmFormState.isCalling
          }
          nextText={isComingFinalStep ? "Confirm & Trade" : "Next"}
        />
      )}
      <ConfirmForm
        closeLightbox={() => setConfirmFormState(false, false)}
        actionFunction={tradeTokens}
        values={{
          headline: "Are you sure you wish to make a trade?",
          paragraph: `Please note that this operation is irreversible.`,
          actionButton: "Confirm",
        }}
        isVisible={confirmFormState.isDisplayed}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: Function) => ({
  updateWalletsAfterTransaction: (data: IUpdateWalletsData) => {
    dispatch(updateWalletsBalanceAfterCryptoTradeTransaction(data));
  },
});

export default connect(undefined, mapDispatchToProps)(ExchangeForm);
